import { useState, useMemo } from 'react';

import CheckForm from '@/components/Check';
import Input from '@/components/input';
import InputPhone from '@/components/InputPhone';
import Volanta from '@/components/Volanta';
import BotonFormUnico from '@/components/Botones/Formulario/Unico';
import cn from 'classnames';
import axios from 'axios';
import { isEmail, isPhone, isString } from '@/utils/common';
import { normalizeEmail } from '@/utils/normalizeEmail';
import RecaptchaLegend from '@/components/RecaptchaLegend';
import { useValidateRecaptcha } from '@/hooks/useValidateRecaptcha';

const Form = ({ finish, country_short_name, form: form_name }) => {
    const { validateRecaptcha } = useValidateRecaptcha();
    const [form, setForm] = useState({});
    const [check, setCheck] = useState(false);
    const [loading, setLoading] = useState(false);

    const handlePhoneChange = (e) => {
        setForm((prevForm) => ({
            ...prevForm,
            phone: e.target.value,
            isValidPhone: e.target.isValid,
        }));
        setCheck(e.target.isValid);
    };

    const isFormComplete = useMemo(() => {
        setCheck(false);

        return (
            isString(form.name) &&
            form.isValidPhone &&
            isEmail(form.email) &&
            isString(form.message, 10) &&
            (form.puntohop || form.ecommerce || form.comprador)
        );
    }, [form]);

    const onChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const onChangeCheckbox = (e) => {
        setForm({
            ...form,
            puntohop: false,
            ecommerce: false,
            comprador: false,
            [e.target.name]: !form[e.target.name],
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!isFormComplete) {
            setCheck(true);
            return;
        }
        setLoading(true);

        try {
            const result = await validateRecaptcha();

            if (!result.success) {
                console.error('Error validando reCAPTCHA:');
                setLoading(false);
                return;
            }
            await axios.post(location.origin + '/api/contact', {
                ...form,
                email: normalizeEmail(form.email),
                type: form.puntohop
                    ? 'Punto HOP'
                    : form.ecommerce
                      ? 'Ecommerce'
                      : 'Comprador',
            });
            finish();
            setLoading(false);
        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit}>
            <div className='sm:hidden mb-30'>
                <Volanta label='soy / quiero ser' />
            </div>
            <div className='flex items-center mb-50 duration-30'>
                <CheckForm
                    label='Punto HOP'
                    name='puntohop'
                    onChange={onChangeCheckbox}
                    validation={check}
                    value={form.puntohop}
                />
                <CheckForm
                    label='ecommerce'
                    name='ecommerce'
                    onChange={onChangeCheckbox}
                    validation={check}
                    value={form.ecommerce}
                />
                <CheckForm
                    label='Comprador'
                    name='comprador'
                    onChange={onChangeCheckbox}
                    validation={check}
                    value={form.comprador}
                />
                <span
                    className={cn(
                        'opacity-0 duration-30 text-red text-20 translate-x-10',
                        {
                            '!opacity-100 !translate-x-0':
                                check &&
                                !form.puntohop &&
                                !form.ecommerce &&
                                !form.comprador,
                        }
                    )}
                >
                    *
                </span>
            </div>
            <div className='sm:hidden mb-30'>
                <Volanta label='info personal' />
            </div>
            <div className='mb-24'>
                <Input
                    type='text'
                    name='name'
                    id='name'
                    placeholder='Nombre y Apellido'
                    onChange={onChange}
                    hasError={check && !isString(form.name)}
                />
            </div>
            <div className='mb-24'>
                <InputPhone
                    country_short_name={country_short_name}
                    name='phone'
                    onChange={handlePhoneChange}
                />
            </div>
            <div className='mb-24'>
                <Input
                    type='email'
                    name='email'
                    id='email'
                    placeholder='Email'
                    onChange={onChange}
                    hasError={check && !isEmail(form.email)}
                />
            </div>
            <div className='mb-24'>
                <Input
                    type='text'
                    name='message'
                    placeholder={form_name.message}
                    onChange={onChange}
                    hasError={check && !isString(form.message, 10)}
                />
            </div>
            <div className='flex justify-center'>
                <div className='max-w-[21rem]'>
                    <BotonFormUnico
                        key={'submit-contact-form'}
                        bgColor='button-gradient'
                        type='submit'
                        text={loading ? 'Enviando...' : 'Enviar consulta'}
                        disabled={!isFormComplete || loading}
                    />
                </div>
            </div>
            <RecaptchaLegend />
        </form>
    );
};

export default Form;
